import "./accounts.scss";

import { WalletCards } from "lucide-react";
import { FunctionComponent, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchClientDepositAccounts } from "../../../store/DepositAccountSlice";
import { DepositAccount, DepositAccountType } from "../../../models/DepositAccount";
import { DataList, DataListColumn, StatusBadge, CopyButton } from "../../../components/ui";
import { formatShortId } from "../../../utils/format";

// Conta + nome da empresa, para a listagem plana (cliente pode estar em mais de uma empresa).
type AccountRow = DepositAccount & { organizationName: string };

const UserAccounts: FunctionComponent = () => {
    const dispatch = useAppDispatch();

    // Contas de deposito das empresas as quais o cliente esta vinculado.
    // Cliente NAO cadastra contas — esta view e somente leitura.
    const clientGroups = useAppSelector((s) => s.depositAccount.clientGroups);

    useEffect(() => {
        dispatch(fetchClientDepositAccounts());
    }, [dispatch]);

    const rows: AccountRow[] = useMemo(
        () =>
            (clientGroups ?? []).flatMap((g) =>
                g.accounts.map((a) => ({ ...a, organizationName: g.organizationName })),
            ),
        [clientGroups],
    );

    const columns: DataListColumn<AccountRow>[] = [
        {
            key: "empresa",
            header: "Empresa",
            width: "1.1fr",
            render: (r) => <strong>{r.organizationName}</strong>,
        },
        {
            key: "apelido",
            header: "Apelido",
            render: (r) => r.nickname,
        },
        {
            key: "tipo",
            header: "Tipo",
            width: "0.7fr",
            render: (r) => (
                <StatusBadge
                    tone={r.type === DepositAccountType.CRYPTO ? "info" : "success"}
                    label={r.type}
                />
            ),
        },
        {
            key: "detalhes",
            header: "Detalhes",
            width: "1.8fr",
            render: (r) =>
                r.type === DepositAccountType.FIAT ? (
                    <span className="cell-stack">
                        <strong>{r.bank?.name}</strong>
                        <span className="acc-inline">
                            Ag. {r.agency}
                            <CopyButton value={r.agency ?? ""} size={12} title="Copiar agencia" />
                            · Conta {r.account}
                            <CopyButton value={r.account ?? ""} size={12} title="Copiar conta" />
                            · {r.transfer_type}
                        </span>
                    </span>
                ) : (
                    <span className="cell-stack">
                        <strong>{r.network?.name}</strong>
                        <span className="acc-inline">
                            {formatShortId(r.hash ?? "", 8)}
                            <CopyButton value={r.hash ?? ""} size={12} title="Copiar hash" />
                        </span>
                    </span>
                ),
        },
    ];

    return (
        <div className="user-accounts">

            <h1 className="page-title">
                <div className="title">
                    <WalletCards size={24} /> Contas das empresas
                </div>
                <small>contas para deposito das empresas as quais voce esta vinculado. somente as empresas cadastram contas.</small>
            </h1>

            <DataList
                columns={columns}
                rows={rows}
                loading={clientGroups === null}
                rowKey={(r: AccountRow) => r.id}
                empty="Sem nenhuma conta disponivel."
            />

        </div>
    );
};

export default UserAccounts;
